import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as SparkleIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  CheckCircle,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { ApiKeysService } from '../../../services/apiKeysService';

interface ApiTestPopupProps {
  open: boolean;
  onClose: () => void;
  apiKey: string;
  service?: string;
}

const ApiTestPopup: React.FC<ApiTestPopupProps> = ({ open, onClose, apiKey, service = 'mistral' }) => {
  const [testing, setTesting] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [result, setResult] = useState<{
    success: boolean;
    poem?: string;
    error?: string;
    userMessage?: string;
    model?: string;
    usage?: any;
  } | null>(null);
  const [connectionResult, setConnectionResult] = useState<{
    success: boolean;
    message?: string;
    error?: string;
  } | null>(null);
  
  // AbortController refs for canceling requests
  const testAbortController = useRef<AbortController | null>(null);
  const connectionAbortController = useRef<AbortController | null>(null);

  const maskApiKey = (key: string) => {
    if (key.length <= 8) return '••••••••';
    return key.substring(0, 4) + '••••••••' + key.substring(key.length - 4);
  };

  const getServiceName = (serviceType: string) => {
    switch (serviceType) {
      case 'mistral':
        return 'Mistral AI';
      case 'groq':
        return 'Groq';
      case 'openai':
        return 'OpenAI';
      case 'anthropic':
        return 'Anthropic';
      case 'google':
        return 'Google AI';
      case 'azure':
        return 'Azure OpenAI';
      default:
        return serviceType.charAt(0).toUpperCase() + serviceType.slice(1);
    }
  };

  const handleTest = async () => {
    // Cancel any existing test
    if (testAbortController.current) {
      testAbortController.current.abort();
    }
    
    // Create new AbortController
    testAbortController.current = new AbortController();
    
    setTesting(true);
    setResult(null);

    try {
      const testResult = await ApiKeysService.testMistralApiKey(apiKey, testAbortController.current.signal);
      setResult(testResult);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        setResult({
          success: false,
          error: 'Test cancelled by user',
          userMessage: 'API test was cancelled'
        });
      } else {
        setResult({
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error occurred'
        });
      }
    } finally {
      setTesting(false);
      testAbortController.current = null;
    }
  };

  const handleTestConnection = async () => {
    // Cancel any existing connection test
    if (connectionAbortController.current) {
      connectionAbortController.current.abort();
    }
    
    // Create new AbortController
    connectionAbortController.current = new AbortController();
    
    setTestingConnection(true);
    setConnectionResult(null);

    try {
      const testResult = await ApiKeysService.testConnection(connectionAbortController.current.signal);
      setConnectionResult(testResult);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        setConnectionResult({
          success: false,
          error: 'Connection test cancelled by user',
          message: 'Connection test was cancelled'
        });
      } else {
        setConnectionResult({
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error occurred'
        });
      }
    } finally {
      setTestingConnection(false);
      connectionAbortController.current = null;
    }
  };

  const handleCancelTest = () => {
    if (testAbortController.current) {
      testAbortController.current.abort();
    }
  };

  const handleCancelConnection = () => {
    if (connectionAbortController.current) {
      connectionAbortController.current.abort();
    }
  };

  const handleClose = () => {
    // Cancel any ongoing operations
    if (testAbortController.current) {
      testAbortController.current.abort();
    }
    if (connectionAbortController.current) {
      connectionAbortController.current.abort();
    }
    
    setResult(null);
    setConnectionResult(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          minHeight: 400,
        }
      }}
    >
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SparkleIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Test {getServiceName(service)} API
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          {/* API Key Information */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.02) 0%, rgba(0, 0, 0, 0.01) 100%)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: 'text.primary' }}>
              Testing API Key
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 80 }}>
                  Provider:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {getServiceName(service)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 80 }}>
                  API Key:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    color: 'text.primary',
                  }}
                >
                  {maskApiKey(apiKey)}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Test Description */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(99, 102, 241, 0.02) 100%)',
              border: '1px solid rgba(99, 102, 241, 0.1)',
            }}
          >
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
              This will test your {getServiceName(service)} API key by generating a short poem. The test will verify:
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0 }}>
              <Typography component="li" variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                API key validity and authentication
              </Typography>
              <Typography component="li" variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Connection to {getServiceName(service)}'s servers
              </Typography>
              <Typography component="li" variant="body2" sx={{ color: 'text.secondary' }}>
                AI model response generation
              </Typography>
            </Box>
          </Paper>

          {/* Test Buttons */}
          {!testing && !testingConnection && !result && !connectionResult && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleTestConnection}
                  startIcon={<CheckCircle />}
                  sx={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    },
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Test Connection
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleTest}
                  startIcon={<SparkleIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5B5BD6 0%, #7C3AED 100%)',
                    },
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Test API Key
                </Button>
              </Box>
            </Box>
          )}

          {/* Loading State */}
          <AnimatePresence>
            {testing && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 4,
                    gap: 2,
                  }}
                >
                  <CircularProgress
                    size={48}
                    sx={{
                      color: '#6366F1',
                      '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round',
                      },
                    }}
                  />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    Testing API Connection...
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    Connecting to Mistral AI and generating a poem
                  </Typography>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Connection Test Loading State */}
          <AnimatePresence>
            {testingConnection && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 4,
                    gap: 2,
                  }}
                >
                  <CircularProgress
                    size={48}
                    sx={{
                      color: '#10B981',
                      '& .MuiCircularProgress-circle': {
                        strokeLinecap: 'round',
                      },
                    }}
                  />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
                    Testing Connection...
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                    Verifying Edge Function connectivity
                  </Typography>
                </Box>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success Result */}
          <AnimatePresence>
            {result?.success && result.poem && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(34, 197, 94, 0.02) 100%)',
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <SuccessIcon sx={{ color: '#22C55E', fontSize: 24 }} />
                    <Typography variant="h6" sx={{ color: '#22C55E', fontWeight: 'bold' }}>
                      API Test Successful!
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Your {getServiceName(service)} API key is working correctly. Here's a poem generated by {getServiceName(service)} AI:
                  </Typography>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(0, 0, 0, 0.05)',
                      position: 'relative',
                      '&::before': {
                        content: '"❝"',
                        position: 'absolute',
                        top: -10,
                        left: 20,
                        fontSize: 32,
                        color: '#6366F1',
                        opacity: 0.3,
                      },
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontStyle: 'italic',
                        lineHeight: 1.8,
                        color: 'text.primary',
                        textAlign: 'center',
                        fontFamily: 'Georgia, serif',
                      }}
                    >
                      {result.poem}
                    </Typography>
                  </Paper>

                  {result.model && (
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        Generated using {result.model}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Connection Test Success Result */}
          <AnimatePresence>
            {connectionResult?.success && connectionResult.message && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <CheckCircle sx={{ color: '#10B981', fontSize: 24 }} />
                    <Typography variant="h6" sx={{ color: '#10B981', fontWeight: 'bold' }}>
                      Connection Test Successful!
                    </Typography>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Your Edge Function connection is working correctly. Response received:
                  </Typography>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      background: 'rgba(16, 185, 129, 0.05)',
                      border: '1px solid rgba(16, 185, 129, 0.1)',
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.8,
                        color: 'text.primary',
                        textAlign: 'center',
                        fontWeight: 600,
                      }}
                    >
                      {connectionResult.message}
                    </Typography>
                  </Paper>
                </Paper>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Connection Test Error Result */}
          <AnimatePresence>
            {connectionResult && !connectionResult.success && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <Alert
                  severity="error"
                  icon={<ErrorIcon />}
                  sx={{
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      width: '100%',
                    },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Connection Test Failed
                  </Typography>
                  <Typography variant="body2">
                    {connectionResult.error || 'An unknown error occurred while testing the connection.'}
                  </Typography>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Result */}
          <AnimatePresence>
            {result && !result.success && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
              >
                <Alert
                  severity="error"
                  icon={<ErrorIcon />}
                  sx={{
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      width: '100%',
                    },
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    API Test Failed
                  </Typography>
                  <Typography variant="body2">
                    {result.userMessage || result.error || 'An unknown error occurred while testing the API key.'}
                  </Typography>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          sx={{ borderRadius: 2 }}
        >
          Close
        </Button>
        
        {/* Cancel buttons for ongoing operations */}
        {testing && (
          <Button
            onClick={handleCancelTest}
            variant="outlined"
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Cancel Test
          </Button>
        )}
        
        {testingConnection && (
          <Button
            onClick={handleCancelConnection}
            variant="outlined"
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Cancel Connection Test
          </Button>
        )}
        
        {/* Retry buttons */}
        {connectionResult && !testingConnection && (
          <Button
            onClick={handleTestConnection}
            variant="outlined"
            disabled={testingConnection}
            sx={{ borderRadius: 2 }}
          >
            Test Connection Again
          </Button>
        )}
        {result && !testing && (
          <Button
            onClick={handleTest}
            variant="outlined"
            disabled={testing}
            sx={{ borderRadius: 2 }}
          >
            Test Again
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ApiTestPopup;
